import { useGetUserSettings } from '@api/user-settings';
import {
  InteractionRequiredAuthError,
  InteractionType,
} from '@azure/msal-browser';
import { useMsalAuthentication } from '@azure/msal-react';
import { UserSettings } from '@models/user-settings';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useStateObj } from '@utils';
import { memo, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loginRequest } from '../../auth/msalConfig';
import { UserSettingsContext } from './useUserSettings';

interface AppInitializationProviderProps {
  children: ReactNode;
}

export const AppInitializationProvider = memo(
  ({ children }: Readonly<AppInitializationProviderProps>) => {
    const { t } = useTranslation();
    const [showContent, setShowContent] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const currentUserSettings = useStateObj<UserSettings>();

    // Initialize MSAL authentication with SSO silent
    const { login, error: authError } = useMsalAuthentication(
      InteractionType.Silent,
      loginRequest,
    );

    // Load user settings
    const { result: userSettings } = useGetUserSettings();

    useEffect(() => {
      if (userSettings) {
        currentUserSettings.set(userSettings);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSettings]);

    useEffect(() => {
      if (authError instanceof InteractionRequiredAuthError) {
        login(InteractionType.Redirect, loginRequest);
      }
    }, [authError, login]);

    const isLoading = currentUserSettings.value == null;

    useEffect(() => {
      if (!isLoading && !showContent) {
        setFadeOut(true);
        const timer = setTimeout(() => {
          setShowContent(true);
        }, 300);

        return () => clearTimeout(timer);
      }
    }, [isLoading, showContent]);

    if (!showContent) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          gap={2}
          sx={{
            transition: 'opacity 0.3s ease-in-out',
            opacity: fadeOut ? 0 : 1,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="textSecondary">
            {t('common.loading')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          opacity: 0,
          animation: 'fadeIn 0.4s ease-in-out forwards',
          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}
      >
        <UserSettingsContext.Provider value={currentUserSettings}>
          {children}
        </UserSettingsContext.Provider>
      </Box>
    );
  },
);
